import React, { ReactElement } from 'react';
import { MatchStrategy, PricingVersion, PropagateMatchOption } from '../types/versions';
import { MarketElements, RuleBasedPricingRule } from '../GlobalJobMatchingService';
import { DataCutPriority, JobsSelection, Methodology, PricingRule } from '../types/rules';
import { numberOptions, textOptions } from 'ps-components/FieldFilters/FieldFilterService';
import { FieldFilter } from 'ps-components/FieldFilters/IFieldFilter';

export function DefineSurveyJobMatchesStep(pricingVersionDetails: PricingVersion): ReactElement {
  const { matchStrategy, shouldPropagate, propagateMatchOption, propagateMatchJobSelection } = pricingVersionDetails;

  function MatchPropagationEntry(): ReactElement {
    if (!shouldPropagate) {
      return <></>;
    }
    if (propagateMatchOption === PropagateMatchOption.ALL) {
      return <li>Propagate to all other jobs</li>;
    } else if (propagateMatchOption === PropagateMatchOption.SELECT_JOBS) {
      return <li>{`Propagate to selected jobs: ${propagateMatchJobSelection?.length ? propagateMatchJobSelection.join(', ') : 'none'}`}</li>
    }
    return <></>;
  }

  return (
    <div className="pricing-version-details__step-rule-container">
      <ul className="pricing-version-details__step-details">
        {matchStrategy === MatchStrategy.CURRENT && <li>Use current matches</li>}
        {matchStrategy === MatchStrategy.FILE && <li>Use match priorities</li>}
        {MatchPropagationEntry()}
      </ul>
    </div>
  );
}

interface CreatePricingRulesStepProps {
  pricingVersionRules: PricingRule[];
  groups: any;
  jobPricingRules: RuleBasedPricingRule[];
  marketElements: MarketElements;
}

export function CreatePricingRulesStep({
  pricingVersionRules,
  jobPricingRules,
  groups,
  marketElements
}: CreatePricingRulesStepProps): ReactElement[] {
  function JobSelectionEntry(jobsSelection: JobsSelection): ReactElement {
    return (
      <>
        {jobsSelection === JobsSelection.ALL_JOBS && <li>All jobs</li>}
        {jobsSelection === JobsSelection.SELECT_JOBS && <li>Select jobs</li>}
      </>
    );
  }

  function DataCutPriorityEntry(dataCutPriority: DataCutPriority, dataCutPrioritySelection: string): ReactElement {
    return (
      <>
        {dataCutPriority === DataCutPriority.DEFAULT && <li>Default data cut priorities</li>}
        {dataCutPriority === DataCutPriority.GROUP_SPECIFIC && dataCutPrioritySelection && (
          <li>
            Group &quot;
            {groups.find((group: { id: string; name: string }) => dataCutPrioritySelection == group.id)?.name}
            &quot; data cut priorities
          </li>
        )}
        {dataCutPriority === DataCutPriority.RULES_BASED_PRICING && dataCutPrioritySelection && (
          <li>
            Rule &quot;
            {jobPricingRules?.find((jobRule: RuleBasedPricingRule) => dataCutPrioritySelection === jobRule.id)?.name}
            &quot; data cut priorities
          </li>
        )}
      </>
    );
  }

  function MethodologyEntry(methodology: Methodology): ReactElement {
    return (
      <>
        {methodology === Methodology.MATCH_PRIORITY && <li>Prioritize match priorities over data cut priorities</li>}
        {methodology === Methodology.DATA_CUT_PRIORITY && <li>Prioritize data cut priorities over match priorities</li>}
      </>
    );
  }

  function filterEntry(incumbentsMinimum: number, companiesMinimum: number): ReactElement {
    return (
      <>
        {(incumbentsMinimum || companiesMinimum) && (
          <li>
            <div>Minimum of:</div>
            {incumbentsMinimum && <div>{incumbentsMinimum} incumbents</div>}
            {companiesMinimum && <div>{companiesMinimum} companies</div>}
          </li>
        )}
      </>
    );
  }

  function marketDataElementsEntry(marketDataElements: Array<string>): ReactElement {
    return (
      <>
        {marketElements.payElements?.length > 0 && marketDataElements?.length > 0 && (
          <li>
            <div>Required Elements:</div>
            {marketDataElements
              .map(
                (elementSource: string) =>
                  marketElements.payElements.find(mainElement => mainElement.source_field === elementSource)
                    ?.field_title
              )
              .join(', ')}
          </li>
        )}
      </>
    );
  }

  function jobsFiltersEntry(jobFilters: typeof FieldFilter[]): ReactElement {
    return (
      <>
        {jobFilters?.length > 0 && (
          <li>
            <div>Job Filters:</div>
            {jobFilters
              .map(
                (filter) => {
                  const operatorList = filter.type === 'text' ? textOptions : numberOptions;
                  const operatorText = operatorList.find((operator: any) => operator.key === filter.operator)?.text;
                  return `${filter.element} ${operatorText} ${filter.value}`
                }
              )
              .join(', ')}
          </li>
        )}
      </>
    );
  }

  return pricingVersionRules.map((rule: PricingRule, index: number) => (
    <div key={'rule-' + index} className="pricing-version-details__step-rule-container">
      <div className="pricing-version-detaills_step-rule-title">{rule.name}</div>
      <ul className="pricing-version-details__step-details">
        {JobSelectionEntry(rule.jobsSelection)}
        {rule.jobsSelection === JobsSelection.SELECT_JOBS && jobsFiltersEntry(rule.jobsFilter)}
        {DataCutPriorityEntry(rule.dataCutPriority, rule.dataCutPrioritySelection)}
        {MethodologyEntry(rule.methodology)}
        {filterEntry(rule.incumbentsMinimum, rule.companiesMinimum)}
        {marketDataElementsEntry(rule.marketDataElements)}
      </ul>
    </div>
  ));
}
